import type { IntlShape } from 'react-intl';
import type { GenerationModelDetails } from '~/domain/dicom/dicomBase';
import type {
    RecordedDatasetOrigin,
    RecordedUser,
} from '~/domain/provenance/RecordedDatasetOrigin';
import type { CatalogMessageId } from '~/i18n';
import { formatDateTime } from '~/utils/intlDateTime';

/** One label/value line describing how a dataset came to exist. */
export interface ProvenanceRow {
    readonly labelId: CatalogMessageId;
    readonly value: string;
}

/** Whether the model produced a structure, and whether its contour has been edited since. */
export interface StructureGeneration {
    readonly modelGenerated: boolean;
    readonly contourEditedSinceGeneration: boolean;
}

export interface ProvenanceInput {
    /** The connector's record; null when none was recorded or none is known yet. */
    readonly recorded: RecordedDatasetOrigin | null;
    /**
     * The model that produced the dataset in this session, before it was
     * saved. Used only when nothing is recorded.
     */
    readonly sessionModel?: GenerationModelDetails;
    /**
     * For each structure, whether the AI model produced it and whether its
     * contour has been edited since — as recorded, never read from the ROI
     * Generation Algorithm tag.
     */
    readonly structures: readonly StructureGeneration[];
}

/**
 * A recorded user for display: the name when the connector verified it,
 * marked as unverified when it did not, "not recorded" when there is none.
 */
export function formatRecordedUser(
    intl: IntlShape,
    user: RecordedUser | null
): string {
    if (!user) {
        return intl.formatMessage({ id: 'provenance.notRecorded' });
    }
    return user.verified
        ? user.name
        : intl.formatMessage(
              { id: 'provenance.user.unverified' },
              { name: user.name }
          );
}

/** A recorded time for display, or "not recorded" when there is none. */
export function formatRecordedTime(intl: IntlShape, date: Date | null): string {
    return date
        ? formatDateTime(intl, date)
        : intl.formatMessage({ id: 'provenance.notRecorded' });
}

/**
 * The lines that say how a dataset came to exist, for display. Every claim is
 * qualified as strongly as the record supports: a version the processing
 * service did not confirm says so, a user the connector did not verify says
 * so, and a dataset without a recorded origin says that rather than showing
 * a guess.
 */
export function provenanceRows(
    intl: IntlShape,
    { recorded, sessionModel, structures }: ProvenanceInput
): ProvenanceRow[] {
    const message = (id: CatalogMessageId, values?: Record<string, string>) =>
        intl.formatMessage({ id }, values);
    const notRecorded = message('provenance.notRecorded');
    const user = (recordedUser: RecordedUser | null) =>
        formatRecordedUser(intl, recordedUser);
    const when = (date: Date | null) => formatRecordedTime(intl, date);
    // The catalogue's display name reads better; the selector beside it is
    // what the backend actually ran, so it stays visible.
    const modelName = (label: string | null, selector: string | null) =>
        label && selector && label !== selector
            ? `${label} (${selector})`
            : (label ?? selector ?? notRecorded);
    // How many of the structures the model produced have had their contours
    // changed since; omitted when the record names none as the model's.
    const editedModelStructures = (): ProvenanceRow[] => {
        const modelStructures = structures.filter((s) => s.modelGenerated);
        if (modelStructures.length === 0) {
            return [];
        }
        const edited = modelStructures.filter(
            (s) => s.contourEditedSinceGeneration
        ).length;
        return [
            {
                labelId: 'provenance.editedModelStructures',
                value: message('provenance.editedModelStructures.count', {
                    edited: String(edited),
                    total: String(modelStructures.length),
                }),
            },
        ];
    };

    if (!recorded) {
        if (sessionModel) {
            return [
                {
                    labelId: 'provenance.origin',
                    value: message('provenance.kind.unsavedAiResult'),
                },
                {
                    labelId: 'provenance.model',
                    value: sessionModel.label || sessionModel.name,
                },
                {
                    labelId: 'provenance.modelVersion',
                    value: sessionModel.version
                        ? message('provenance.modelVersion.listed', {
                              version: sessionModel.version,
                          })
                        : notRecorded,
                },
                ...editedModelStructures(),
            ];
        }
        return [
            {
                labelId: 'provenance.origin',
                value: message('provenance.kind.notRecorded'),
            },
        ];
    }

    switch (recorded.kind) {
        case 'AiTask': {
            const rows: ProvenanceRow[] = [
                {
                    labelId: 'provenance.origin',
                    value: message('provenance.kind.aiTask'),
                },
                {
                    labelId: 'provenance.model',
                    value: modelName(recorded.modelLabel, recorded.model),
                },
                {
                    labelId: 'provenance.modelVersion',
                    value: !recorded.modelVersion
                        ? notRecorded
                        : recorded.modelVersionConfirmed
                          ? recorded.modelVersion
                          : message('provenance.modelVersion.listed', {
                                version: recorded.modelVersion,
                            }),
                },
            ];
            rows.push({
                labelId: 'provenance.modelResult',
                value: message(
                    recorded.modelResultKept
                        ? 'provenance.modelResult.kept'
                        : 'provenance.modelResult.notKept'
                ),
            });
            if (recorded.modelCustomization) {
                rows.push({
                    labelId: 'provenance.customization',
                    value: recorded.modelCustomization,
                });
            }
            rows.push(
                {
                    labelId: 'provenance.requestedStructures',
                    value:
                        recorded.requestedStructures.length > 0
                            ? recorded.requestedStructures.join(', ')
                            : message(
                                  'provenance.requestedStructures.modelDefault'
                              ),
                },
                {
                    labelId: 'provenance.requestedBy',
                    value: user(recorded.requestedBy),
                },
                {
                    labelId: 'provenance.requestedAt',
                    value: when(recorded.requestedAt),
                },
                {
                    labelId: 'provenance.generatedAt',
                    value: when(recorded.generatedAt),
                },
                {
                    labelId: 'provenance.savedBy',
                    value: user(recorded.savedBy),
                },
                { labelId: 'provenance.savedAt', value: when(recorded.savedAt) }
            );
            rows.push(...editedModelStructures());
            return rows;
        }
        case 'CreatedInApp':
            return [
                {
                    labelId: 'provenance.origin',
                    value: message('provenance.kind.createdInApp'),
                },
                {
                    labelId: 'provenance.createdBy',
                    value: user(recorded.savedBy),
                },
                {
                    labelId: 'provenance.createdAt',
                    value: when(recorded.savedAt),
                },
            ];
        case 'Duplicate':
            return [
                {
                    labelId: 'provenance.origin',
                    value: message('provenance.kind.duplicate'),
                },
                {
                    labelId: 'provenance.copiedBy',
                    value: user(recorded.savedBy),
                },
                {
                    labelId: 'provenance.copiedAt',
                    value: when(recorded.savedAt),
                },
            ];
        case 'Imported': {
            const rows: ProvenanceRow[] = [
                {
                    labelId: 'provenance.origin',
                    value: recorded.importSource
                        ? message('provenance.kind.imported', {
                              source: recorded.importSource,
                          })
                        : message('provenance.kind.importedUnknownSource'),
                },
                {
                    labelId: 'provenance.receivedAt',
                    value: when(recorded.savedAt),
                },
            ];
            const declared = [
                recorded.declaredManufacturer,
                recorded.declaredModelName,
                recorded.declaredSoftwareVersions,
            ]
                .filter(Boolean)
                .join(' · ');
            if (declared) {
                rows.push({
                    labelId: 'provenance.declaredDevice',
                    value: declared,
                });
            }
            return rows;
        }
        case 'Unrecognised':
            return [
                {
                    labelId: 'provenance.origin',
                    value: message('provenance.kind.unrecognised'),
                },
                {
                    labelId: 'provenance.savedAt',
                    value: when(recorded.savedAt),
                },
            ];
    }
}
